import { getJWTToken } from '../../utils/auth';
import { authHeadersFor } from '../../core/impersonation';
import type { CriteriaDifference, DifferenceStatus } from '../Settings/settingsApi';
import { bandLabel, formatMinimum, measureName, schoolYearLabel } from '../Settings/criteriaView';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export type Resolution = Exclude<DifferenceStatus, 'open'>;

export type CriteriaDifferencesRow = {
  districtId: string;
  districtName?: string;
  state?: string;
  stateName?: string;
  stateRecordVersion: string;
  verifiedOn?: string;
  criteriaChangedAt: string;
  items: CriteriaDifference[];
  savedBy: string;
  savedByName?: string;
  savedAt: string;
  status: DifferenceStatus;
  resolvedBy?: string;
  resolvedByName?: string;
  resolvedAt?: string;
};

export const RESOLUTION_LABELS: Record<Resolution, string> = {
  state_record_corrected: 'State record corrected',
  district_specific: 'District-specific',
};

export class DifferencesChangedError extends Error {
  readonly current: CriteriaDifferencesRow | null;

  constructor(current: CriteriaDifferencesRow | null) {
    super('The differences item changed since it was loaded');
    this.name = 'DifferencesChangedError';
    this.current = current;
  }
}

async function staffHeaders(): Promise<Record<string, string>> {
  return authHeadersFor(await getJWTToken(), null);
}

export async function fetchCriteriaDifferences(): Promise<CriteriaDifferencesRow[]> {
  const response = await fetch(`${apiUrl}/admin/criteria-differences`, { headers: await staffHeaders() });
  if (!response.ok) throw new Error(`GET /admin/criteria-differences failed: ${response.status}`);
  const body = (await response.json()) as { districts?: CriteriaDifferencesRow[] } | null;
  return body?.districts ?? [];
}

export async function resolveCriteriaDifferences(
  target: Pick<CriteriaDifferencesRow, 'districtId' | 'criteriaChangedAt'>,
  status: Resolution,
): Promise<CriteriaDifferencesRow> {
  const response = await fetch(`${apiUrl}/admin/criteria-differences/resolve`, {
    method: 'POST',
    headers: { ...(await staffHeaders()), 'Content-Type': 'application/json' },
    body: JSON.stringify({ districtId: target.districtId, criteriaChangedAt: target.criteriaChangedAt, status }),
  });
  if (response.status === 409) {
    const body = (await response.json()) as { district?: CriteriaDifferencesRow } | null;
    throw new DifferencesChangedError(body?.district ?? null);
  }
  if (response.status === 404) throw new DifferencesChangedError(null);
  if (!response.ok) throw new Error(`POST /admin/criteria-differences/resolve failed: ${response.status}`);
  return (await response.json()) as CriteriaDifferencesRow;
}

export type DifferenceLine = { where: string; what: string };

const OUTCOME_LABELS: Record<string, string> = { exits: 'Exits', eligible_for_review: 'Eligible for review' };

function whereLabel(item: CriteriaDifference): string {
  const grades = bandLabel({ assessment: 'access', grades: item.grades, noExit: false, ruleSets: [] });
  if (item.assessment !== 'alternate_access') return grades;
  const all = item.grades === 'all' || item.grades.length === 0;
  return all ? 'Alternate ACCESS' : `Alternate ACCESS · ${grades}`;
}

function minimum(value: number | undefined, alternate: boolean): string {
  return value === undefined ? '' : formatMinimum(value, alternate);
}

function whatLabel(item: CriteriaDifference): string {
  const alternate = item.assessment === 'alternate_access';
  const measure = measureName(item.measure, alternate);
  const district = minimum(item.districtMinimum, alternate);
  const record = minimum(item.recordMinimum, alternate);
  switch (item.kind) {
    case 'changed':
      return `${measure} ${record} → ${district}`;
    case 'added':
      return `Added: ${measure} ≥ ${district}`;
    case 'rule_added':
      return `Added a review rule: ${measure} ≥ ${district}`;
    default:
      return `Removed: ${measure} ≥ ${record}`;
  }
}

export function differenceLines(row: Pick<CriteriaDifferencesRow, 'items'>): DifferenceLine[] {
  return row.items.map((item) => ({
    where: `${whereLabel(item)} · ${OUTCOME_LABELS[item.outcome] ?? item.outcome}`,
    what: whatLabel(item),
  }));
}

export function districtLabel(row: Pick<CriteriaDifferencesRow, 'districtId' | 'districtName'>): string {
  return row.districtName || row.districtId;
}

export function stateLine(row: Pick<CriteriaDifferencesRow, 'state' | 'stateName' | 'stateRecordVersion'>): string {
  const record = `${schoolYearLabel(row.stateRecordVersion)} record`;
  const state = row.stateName || row.state;
  return state ? `${state} · ${record}` : record;
}

export function personLabel(name: string | undefined, sub: string): string {
  return name || sub;
}

export function formatDifferencesDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function newestFirst(a: string, b: string): number {
  return b.localeCompare(a);
}

export function splitByStatus(rows: CriteriaDifferencesRow[]): { open: CriteriaDifferencesRow[]; resolved: CriteriaDifferencesRow[] } {
  const open = rows.filter((r) => r.status === 'open').sort((a, b) => newestFirst(a.savedAt, b.savedAt));
  const resolved = rows
    .filter((r) => r.status !== 'open')
    .sort((a, b) => newestFirst(a.resolvedAt ?? a.savedAt, b.resolvedAt ?? b.savedAt));
  return { open, resolved };
}
